import { Node, Edge } from '../../models/scene';
import { INodeMutationManager, NodeChangeRecord } from '../base/BaseNodeCommand';
import { useDiagramStore } from '../../../stores/diagramStore';
import type * as Y from 'yjs';
import { Logger } from '../../../utils/logger';
import {
  findSiblingBoundShapes,
  getDataBinding,
} from '../../../domains/data-binding/services/BoundShapeEditInterceptor';
import {
  updateBoundRecordChanges,
  getBoundRecordState,
} from '../../../domains/data-binding/stores/BoundRecordStore';
import {
  extractDataChanges,
  createShapeUpdateFromData,
  EXCLUDED_FROM_SYNC,
} from '../../../domains/data-binding/utils/fieldMappings';
import {
  trackDeletedNodeBinding,
  clearDeletedNodeBindings,
} from '../../../domains/data-binding/utils/nodeBinding';
import { ShapeMapAdapter } from '../../../lib/diagram/ShapeMapAdapter';
import {
  InvalidShapeGeometryError,
  ShapePersistenceGateway,
  getInvalidShapeCreationGeometry,
  normalizeShapeGeometryPatch,
} from '../../../lib/diagram/shapeGeometry';
import { isProjectionOwnedNode } from '../../canvas/composeProjectedScene';
import { PROJECTED_LAYOUT_MAP, PROJECTED_POSITIONS_MAP, readProjectedPosition, writeProjectedPosition } from '../../canvas/projectedLayout';

const logger = Logger.getLoggerWithPrefix('NodeMutationManager');

// Re-export for convenience
export type { NodeChangeRecord } from '../base/BaseNodeCommand';

// Managed style keys used across apply/undo paths
const STYLE_KEYS = [
  'fillColor',
  'strokeColor',
  'textColor',
  'fontSize',
  'fontFamily',
  'fontWeight',
  'fontStyle',
  'textDecoration',
  'textAlign',
  'verticalAlign',
  'strokeWidth',
] as const;

const ATTR_KEYS = [
  'strokeWidth',
  'dash',
  'dashEnabled',
  'fontSize',
  'fontFamily',
  'fontWeight',
  'fontStyle',
  'textDecoration',
  'textAlign',
  'verticalAlign',
  'textColor',
] as const;

export function mergeEffectiveNodeAttrs(node: Node): Record<string, unknown> {
  const propsAttrs = (node as any)?.props?.attrs;
  const top = (node as any)?.attrs;
  const fromProps = propsAttrs && typeof propsAttrs === 'object' ? { ...propsAttrs } : {};
  const fromTop = top && typeof top === 'object' ? { ...top } : {};
  return { ...fromProps, ...fromTop };
}

export function collectChangedAttrKeysBetweenAttrSnapshots(
  oldAttrs: Record<string, unknown>,
  newAttrs: Record<string, unknown>,
): Set<string> {
  const keys = new Set([...Object.keys(oldAttrs), ...Object.keys(newAttrs)]);
  const changed = new Set<string>();
  for (const k of keys) {
    const inOld = Object.prototype.hasOwnProperty.call(oldAttrs, k);
    const inNew = Object.prototype.hasOwnProperty.call(newAttrs, k);
    if (inOld !== inNew) {
      changed.add(k);
      continue;
    }
    if (oldAttrs[k] !== newAttrs[k]) {
      changed.add(k);
    }
  }
  return changed;
}

// Small helper to consistently mirror style-related fields into Yjs `style` and `props`.
function computeStyleAndPropsPatches(
  current: any,
  changes: Partial<Node> | undefined
): { stylePatch: Record<string, unknown>; propsPatch: Record<string, unknown> } {
  const stylePatch: Record<string, unknown> = { ...(current?.style || {}) };
  const propsPatch: Record<string, unknown> = { ...(current?.props || {}) };
  if (!changes) return { stylePatch, propsPatch };

  // Known top-level style keys that various renderers/lenses may read
  const topLevelStyleKeys = STYLE_KEYS;

  for (const key of topLevelStyleKeys) {
    if (Object.prototype.hasOwnProperty.call(changes as any, key)) {
      const val = (changes as any)[key];
      if (typeof val !== 'undefined') {
        stylePatch[key as string] = val;
        propsPatch[key as string] = val;
      }
    }
  }

  // Mirror selective attrs into props.attrs (projection readers look there)
  const attrs = (changes as any)?.attrs as Record<string, unknown> | undefined;
  if (attrs && typeof attrs === 'object') {
    const prevAttrs = (current?.props?.attrs as Record<string, unknown>) || {};
    const nextAttrs: Record<string, unknown> = { ...prevAttrs };
  const attrKeys = ATTR_KEYS;
    for (const k of attrKeys) {
      if (Object.prototype.hasOwnProperty.call(attrs, k)) {
        const v = (attrs as any)[k];
        if (typeof v === 'undefined') {
          delete nextAttrs[k as string];
        } else {
          nextAttrs[k as string] = v as unknown;
        }
      }
    }
    if (Object.keys(nextAttrs).length > 0) {
      propsPatch.attrs = nextAttrs;
    }

    // Also mirror style-relevant attr keys to top-level style and props for
    // consistent reads (some renderers coalesce style → attrs).
    for (const k of attrKeys) {
      if (Object.prototype.hasOwnProperty.call(attrs, k)) {
        const v = (attrs as any)[k];
        if (typeof v !== 'undefined') {
          stylePatch[k as string] = v as unknown;
          propsPatch[k as string] = v as unknown;
        } else {
          // Clearing: remove mirrors if present
          if (k in stylePatch) delete stylePatch[k as string];
          if (k in propsPatch) delete propsPatch[k as string];
        }
      }
    }
  }

  return { stylePatch, propsPatch };
}

// When commands provide `attrs`, treat them as the full authoritative snapshot
// for mirrored `props.attrs` so removed keys stay removed through Yjs projection.
function applyAttrSnapshotToProps(
  prevProps: Record<string, unknown> | undefined,
  nextProps: Record<string, unknown>,
  changes: Partial<Node> | undefined
): Record<string, unknown> {
  if (!(changes as any)?.attrs) {
    return nextProps;
  }

  return {
    ...(prevProps || {}),
    ...nextProps,
    attrs: { ...(((changes as any).attrs || {}) as Record<string, unknown>) },
  };
}

/**
 * Edge change record for undo/redo
 */
export interface EdgeChangeRecord {
  edgeId: string;
  oldState: Partial<Edge> | null;
  newState: Partial<Edge>;
}

/**
 * Manages node and edge mutations in a reversible way for undo/redo support
 * This is the ONLY place where nodes and edges should be mutated during command execution
 * 
 * Architecture: Commands write to Yjs (source of truth), which triggers observers
 * that sync to stores for UI rendering.
 */
export class NodeMutationManager implements INodeMutationManager {
  private ydoc?: Y.Doc;
  public canWrite: boolean = true; // Default to true, will be set by context
  
  runInTransaction<T>(fn: () => T, origin: unknown = 'local'): T {
    if (this.ydoc) {
      let result!: T;
      this.ydoc.transact(() => {
        result = fn();
      }, origin);
      return result;
    }
    return fn();
  }

  /**
   * Get the Yjs document (for binding operations that need direct doc access)
   */
  getDoc(): Y.Doc | undefined {
    return this.ydoc;
  }

  /**
   * Set the Yjs document for persistence
   */
  private documentBindingVersion = 0;

  getDocumentBindingVersion(): number { return this.documentBindingVersion; }

  setYDoc(ydoc?: Y.Doc): void {
    this.documentBindingVersion++;
    const hadDoc = !!this.ydoc;
    this.ydoc = ydoc;
    if (!hadDoc && ydoc) {
      // Yjs document connected
      try {
        const shapes = ydoc.getMap('shapes');
        const connectors = ydoc.getMap('connectors');
        logger.debug('Debug SDI: NodeMutationManager.setYDoc connected', {
          shapes: shapes?.size,
          connectors: connectors?.size
        });
      } catch {}
    }
  }

  /**
   * Apply batch changes to multiple nodes in a single store update
   */
  applyBatchNodeChanges(updates: Array<{ nodeId: string; changes: Partial<Node> }>): NodeChangeRecord[] {
    const store = useDiagramStore.getState();
    const records: NodeChangeRecord[] = [];
    const batchUpdates: Array<{ id: string; updates: Partial<Node> }> = [];
    const nodesById = new Map(store.nodes.map((node) => [node.id, node]));
    try { logger.debug('batch start', { count: updates.length, ids: updates.map(u => u.nodeId) }); } catch {}
    
    // Prepare all changes and create records
    updates.forEach(({ nodeId, changes: incomingChanges }) => {
      const currentNode = nodesById.get(nodeId);
      if (!currentNode) {
        logger.error(`Node ${nodeId} not found for batch update`);
        return;
      }
      const normalizedGeometry = normalizeShapeGeometryPatch(
        currentNode as unknown as Record<string, unknown>,
        incomingChanges as unknown as Record<string, unknown>,
      );
      const changes = normalizedGeometry.patch as Partial<Node>;
      if (normalizedGeometry.rejectedKeys.length > 0) {
        logger.warn('Rejected non-finite node geometry update', {
          nodeId,
          keys: normalizedGeometry.rejectedKeys,
        });
      }
      if (Object.keys(changes).length === 0) return;
      // Guard: respect locking except for unlocking/visibility toggles
      const keys = Object.keys(changes || {});
      const isGeometryOnlyProjectionUpdate =
        isProjectionOwnedNode(currentNode) &&
        keys.length > 0 &&
        keys.every(k => k === 'x' || k === 'y');
      const isAllowedOnLocked = keys.length > 0 && keys.every(k => k === 'locked' || k === 'visible');
      if (currentNode.locked && !isAllowedOnLocked && !isGeometryOnlyProjectionUpdate) {
        try { logger.warn('Skip update for locked node', nodeId, keys); } catch {}
        return;
      }
      
      const oldState: Partial<Node> = {};
      const newState: Partial<Node> = {};
      
      // Handle nested attrs specially
      if (changes.attrs) {
        const currentAttrs = currentNode.attrs || {};
        const newAttrs = { ...currentAttrs, ...changes.attrs };
        Object.keys(newAttrs).forEach(key => {
          if (newAttrs[key] === undefined) {
            delete newAttrs[key];
          }
        });
        oldState.attrs = currentAttrs;
        newState.attrs = newAttrs;
        try { logger.debug('batch person', { id: nodeId, prevAttrs: currentAttrs, incomingAttrs: changes.attrs, mergedAttrs: newAttrs }); } catch {}
        
        // Prepare the final changes with merged attrs
        const finalChanges = { ...changes, attrs: newAttrs };
        batchUpdates.push({ id: nodeId, updates: finalChanges });
        
        // Record all changes
        Object.keys(finalChanges).forEach(key => {
          const field = key as keyof Node;
          if (field !== 'attrs') {
            oldState[field] = currentNode[field] as any;
          }
          newState[field] = finalChanges[field] as any;
        });
      } else {
        // For non-attrs changes
        Object.keys(changes).forEach(key => {
          const field = key as keyof Node;
          oldState[field] = currentNode[field] as any;
          newState[field] = changes[field] as any;
        });
        batchUpdates.push({ id: nodeId, updates: changes });
      }
      
      records.push({
        nodeId,
        oldState,
        newState
      });
    });
    
    // Apply all updates in a single store update
    if (batchUpdates.length > 0) {
      const currentNodes = new Map(store.nodes.map(n => [n.id, n]));
      const recordsById = new Map(records.map((record) => [record.nodeId, record]));
      // WRITE TO YIJS FIRST (source of truth) in a single batch transaction
      // This ensures we have a single Yjs transaction before any store updates
      // that might trigger additional syncs
      if (this.ydoc) {
        const shapesMap = new ShapePersistenceGateway(new ShapeMapAdapter(this.ydoc.getMap('shapes')));
        const storeFallbackUpdates: Array<{ id: string; updates: Partial<Node> }> = [];
        
        // Apply all updates in a single Yjs transaction
        this.ydoc.transact(() => {
          batchUpdates.forEach(({ id: nodeId, updates: finalChanges }) => {
            const projectedNode = currentNodes.get(nodeId);
            if (projectedNode && isProjectionOwnedNode(projectedNode)
              && Object.keys(finalChanges).every((key) => key === 'x' || key === 'y')
              && this.ydoc!.getMap(PROJECTED_LAYOUT_MAP).get('mode') === 'custom') {
              const position = { x: finalChanges.x ?? projectedNode.x, y: finalChanges.y ?? projectedNode.y };
              writeProjectedPosition(this.ydoc!, nodeId, position);
              const record = recordsById.get(nodeId);
              if (record) record.projectedLayoutDoc = this.ydoc!;
              storeFallbackUpdates.push({ id: nodeId, updates: position });
              return;
            }
            const currentYjsNode = shapesMap.get(nodeId);
            
            if (currentYjsNode) {
              // Merge with existing Yjs state, preserving nested structures used by projection
              // Compute mirrored patches for style and props from this change set
              const { stylePatch, propsPatch } = computeStyleAndPropsPatches(currentYjsNode, finalChanges);
              const prevProps: any = (currentYjsNode as any).props || {};
              let nextProps: any = { ...propsPatch };
              
              // Carry over any explicit props updates from finalChanges.props
              if ((finalChanges as any).props && typeof (finalChanges as any).props === 'object') {
                Object.assign(nextProps, (finalChanges as any).props);
              }
              
              // Mirror text/name/location fields into props for consistency with projection
              if ('text' in finalChanges) nextProps.text = (finalChanges as any).text;
              if ('name' in finalChanges) nextProps.name = (finalChanges as any).name;
              if ('location' in finalChanges) nextProps.location = (finalChanges as any).location;
              nextProps = applyAttrSnapshotToProps(prevProps, nextProps, finalChanges);

              // Create updated node (finalChanges already includes text/name/location from spreading)
              // Spreading currentYjsNode first preserves width/w when updating height
              const updatedNode: any = {
                ...currentYjsNode,
                ...finalChanges,
                style: stylePatch,
                props: { ...prevProps, ...nextProps }
              };
              
              // Ensure width/height are both explicitly set when updating height (Yjs uses width/height as primary fields)
              if ('height' in finalChanges || 'h' in finalChanges) {
                const newHeight = (finalChanges as any).height ?? (finalChanges as any).h;
                if (typeof newHeight === 'number') {
                  updatedNode.height = newHeight;
                  updatedNode.h = newHeight;
                  const existingWidth = (currentYjsNode as any).width ?? (currentYjsNode as any).w;
                  const incomingWidth = (finalChanges as any).width ?? (finalChanges as any).w;
                  const resolvedWidth =
                    typeof incomingWidth === 'number'
                      ? incomingWidth
                      : typeof existingWidth === 'number'
                        ? existingWidth
                        : undefined;
                  if (typeof resolvedWidth === 'number') {
                    updatedNode.width = resolvedWidth;
                    updatedNode.w = resolvedWidth;
                  }
                }
              }

              shapesMap.patch(nodeId, updatedNode);
            } else {
              const currentStoreNode = currentNodes.get(nodeId);
              const { stylePatch, propsPatch } = computeStyleAndPropsPatches(currentStoreNode, finalChanges);
              const prevProps = ((currentStoreNode as any)?.props || {}) as Record<string, unknown>;
              storeFallbackUpdates.push({
                id: nodeId,
                updates: {
                  ...(finalChanges as any),
                  style: stylePatch,
                  props: applyAttrSnapshotToProps(prevProps, propsPatch, finalChanges),
                } as Partial<Node>,
              });
            }
          });
        }, 'local'); // Use 'local' origin to distinguish from electric/awareness/server

        if (storeFallbackUpdates.length > 0) {
          store.updateNodes(storeFallbackUpdates as any);
        }

        // DO NOT update store here - let Yjs → Store sync handle it
        // This prevents triggering store-to-Yjs sync which would create a second transaction
        // The Yjs update event will sync back to store automatically
      } else {
        // No Yjs doc - update store; also mirror style/props for each change
        const withMirrors = batchUpdates.map(({ id, updates }) => {
        const current = currentNodes.get(id);
        const { stylePatch, propsPatch } = computeStyleAndPropsPatches(current, updates);
        const prevProps = ((current as any)?.props || {}) as Record<string, unknown>;
        const nextProps = applyAttrSnapshotToProps(prevProps, propsPatch, updates);
        return {
          id,
          updates: { ...(updates as any), style: stylePatch, props: nextProps } as any,
        };
      });
      store.updateNodes(withMirrors as any);
      }
      
      try {
        const post = useDiagramStore.getState().nodes.filter(n => batchUpdates.some(b => b.id === n.id)).map(n => ({ id: n.id, attrs: (n as any).attrs }));
        logger.debug('batch post-state', post);
      } catch {}
    }
    
    return records;
  }
  
  /**
   * Apply changes to a node and record the change for undo
   */
  applyNodeChange(nodeId: string, changes: Partial<Node>): NodeChangeRecord {
    // Check write permissions first
    if (!this.canWrite) {
      logger.warn(`Write permission denied for node ${nodeId}`);
      return { nodeId, oldState: {}, newState: {} };
    }
    
    // Always get fresh state
    const currentNode = this.getNode(nodeId);
    
    if (!currentNode) {
      logger.error(`Node ${nodeId} not found in store!`);
      throw new Error(`Node ${nodeId} not found`);
    }
    const normalizedGeometry = normalizeShapeGeometryPatch(
      currentNode as unknown as Record<string, unknown>,
      changes as unknown as Record<string, unknown>,
    );
    changes = normalizedGeometry.patch as Partial<Node>;
    if (normalizedGeometry.rejectedKeys.length > 0) {
      logger.warn('Rejected non-finite node geometry update', {
        nodeId,
        keys: normalizedGeometry.rejectedKeys,
      });
    }
    if (Object.keys(changes).length === 0) {
      return { nodeId, oldState: {}, newState: {} };
    }
    
    // Guard: respect locking except for unlocking/visibility toggles
    const keys = Object.keys(changes || {});
    const isGeometryOnlyProjectionUpdate =
      isProjectionOwnedNode(currentNode) &&
      keys.length > 0 &&
      keys.every(k => k === 'x' || k === 'y');
    const isAllowedOnLocked = keys.length > 0 && keys.every(k => k === 'locked' || k === 'visible');
    if (currentNode.locked && !isAllowedOnLocked && !isGeometryOnlyProjectionUpdate) {
      try { logger.warn('Locked node update blocked', { nodeId, keys }); } catch {}
      return { nodeId, oldState: {}, newState: {} };
    }
    
    // Record old state for undo
    const oldState: Partial<Node> = {};
    const newState: Partial<Node> = {};
    
    // Calculate final changes
    let finalChanges: Partial<Node>;
    const currentProps = (currentNode as any).props || {};
    const rawPropsUpdate = (changes as any)?.props;
    const propsUpdate =
      rawPropsUpdate && typeof rawPropsUpdate === 'object'
        ? (rawPropsUpdate as Record<string, unknown>)
        : undefined;

    if (changes.attrs) {
      // Merge props.attrs + top-level attrs (matches projection / image manifest)
      const currentAttrs = mergeEffectiveNodeAttrs(currentNode as Node);
      const newAttrs = { ...currentAttrs, ...changes.attrs };
      Object.keys(newAttrs).forEach(key => {
        if (newAttrs[key] === undefined) {
          delete newAttrs[key];
        }
      });
      
      oldState.attrs = currentAttrs;
      newState.attrs = newAttrs;
      
      // Merge attrs with other changes
      finalChanges = { ...(changes as any), attrs: newAttrs } as Partial<Node>;
    } else {
      // For non-attrs changes, handle normally
      finalChanges = { ...(changes as any) } as Partial<Node>;
    }

    if (propsUpdate) {
      const mergedProps: Record<string, unknown> = { ...currentProps };
      Object.entries(propsUpdate).forEach(([key, value]) => {
        if (value === undefined) {
          delete mergedProps[key];
        } else {
          mergedProps[key] = value;
        }
      });
      finalChanges = { ...(finalChanges as any), props: mergedProps as any } as Partial<Node>;
      oldState.props = currentProps as any;
      newState.props = mergedProps as any;
    }

    // Record all changes
    const finalRecord = finalChanges as Record<string, any>;
    const currentRecord = currentNode as Record<string, any>;
    const oldRecord = oldState as Record<string, any>;
    const newRecord = newState as Record<string, any>;

    Object.keys(finalRecord).forEach(key => {
      if (key === 'attrs') {
        return;
      }
      if (key === 'props') {
        if (!propsUpdate) {
          oldRecord.props = currentProps;
          newRecord.props = finalRecord.props;
        }
        return;
      }
      oldRecord[key] = currentRecord[key];
      newRecord[key] = finalRecord[key];
    });
    
    // WRITE TO YIJS (source of truth)
    // The Yjs observer will sync to stores for UI rendering
    if (this.ydoc && isGeometryOnlyProjectionUpdate
      && this.ydoc.getMap(PROJECTED_LAYOUT_MAP).get('mode') === 'custom') {
      const position = { x: finalChanges.x ?? currentNode.x, y: finalChanges.y ?? currentNode.y };
      this.ydoc.transact(() => writeProjectedPosition(this.ydoc!, nodeId, position), 'local');
      useDiagramStore.getState().updateNode(nodeId, position);
      return { nodeId, oldState, newState, projectedLayoutDoc: this.ydoc };
    }
    if (this.ydoc) {
      const shapesMap = new ShapePersistenceGateway(new ShapeMapAdapter(this.ydoc.getMap('shapes')));
      const currentYjsNode = shapesMap.get(nodeId);

      if (currentYjsNode) {
        this.ydoc.transact(() => {
          // Merge with existing Yjs state, preserving nested structures used by projection
          // Compute mirrored patches for style and props from this change set
          const { stylePatch, propsPatch } = computeStyleAndPropsPatches(currentYjsNode, finalChanges);
          const prevProps: any = (currentYjsNode as any).props || {};
          let nextProps: any = { ...propsPatch };
          // Carry over any explicit props updates from finalChanges.props
          if ((finalChanges as any).props && typeof (finalChanges as any).props === 'object') {
            Object.assign(nextProps, (finalChanges as any).props);
          }
          // Mirror text/name/location fields into props for consistency with projection
          if ('text' in finalChanges) nextProps.text = (finalChanges as any).text;
          if ('name' in finalChanges) nextProps.name = (finalChanges as any).name;
          if ('location' in finalChanges) nextProps.location = (finalChanges as any).location;
          nextProps = applyAttrSnapshotToProps(prevProps, nextProps, finalChanges);

          // Check if shape has a data binding and update the CENTRAL store
          // (not per-shape localChanges - we use centralized BoundRecordStore)
          const binding = getDataBinding(currentYjsNode as Record<string, unknown>);
          console.log('[NodeMutationManager] Checking binding:', {
            nodeId,
            hasBinding: !!binding,
            sourceId: binding?.sourceId,
            recordId: binding?.recordId,
            hasCachedRecord: !!binding?.cachedRecord,
            finalChangesKeys: Object.keys(finalChanges),
          });
          if (binding && binding.sourceId && binding.recordId && binding.cachedRecord) {
            // Extract data-related changes using field mappings
            const existingLocalState = this.ydoc
              ? getBoundRecordState(this.ydoc, binding.sourceId, binding.recordId)
              : undefined;
            const extractionBaseRecord =
              binding.isNewRecord && existingLocalState?.localChanges
                ? { ...binding.cachedRecord, ...existingLocalState.localChanges }
                : binding.cachedRecord;

            const dataSourceChanges = extractDataChanges(
              finalChanges as Record<string, unknown>,
              undefined,
              extractionBaseRecord
            );
            console.log('[NodeMutationManager] Extracted data changes:', dataSourceChanges);

            if (Object.keys(dataSourceChanges).length > 0) {
              // Update the CENTRAL bound record store (single source of truth)
              // This will be visible to all shapes bound to this record
              if (this.ydoc) {
                updateBoundRecordChanges(
                  this.ydoc,
                  binding.sourceId,
                  binding.recordId,
                  dataSourceChanges,
                  binding.cachedRecord,
                  'user'
                );
              }
              console.log('[NodeMutationManager] Updated central bound record store', {
                sourceId: binding.sourceId,
                recordId: binding.recordId,
                dataSourceChanges,
              });
            }
          }

          // 3) Create updated node (finalChanges already includes text/name/location from spreading)
          // Spreading currentYjsNode first preserves width/w when updating height
          const updatedNode: any = {
            ...currentYjsNode,
            ...finalChanges,
            style: stylePatch,
            props: { ...prevProps, ...nextProps }
          };

          // Ensure width/height are both explicitly set when updating height (Yjs uses width/height as primary fields)
          // This ensures Yjs detects the change and persists it correctly
          if ('height' in finalChanges || 'h' in finalChanges) {
            const newHeight = (finalChanges as any).height ?? (finalChanges as any).h;
            if (typeof newHeight === 'number') {
              // Explicitly set height to ensure Yjs detects the change
              updatedNode.height = newHeight;
              updatedNode.h = newHeight;
              // Ensure width is explicitly preserved or updated when provided alongside height
              const existingWidth = (currentYjsNode as any).width ?? (currentYjsNode as any).w;
              const incomingWidth = (finalChanges as any).width ?? (finalChanges as any).w;
              const resolvedWidth =
                typeof incomingWidth === 'number'
                  ? incomingWidth
                  : typeof existingWidth === 'number'
                    ? existingWidth
                    : undefined;
              if (typeof resolvedWidth === 'number') {
                updatedNode.width = resolvedWidth;
                updatedNode.w = resolvedWidth;
              }
            }
          }

          shapesMap.patch(nodeId, updatedNode);
        });
      } else {
        logger.debug(`Node ${nodeId} not in Yjs yet, using store fallback`);
        const store = useDiagramStore.getState();
        const current = store.nodes.find(n => n.id === nodeId);
        const { stylePatch, propsPatch } = computeStyleAndPropsPatches(current, finalChanges);
        const mergedProps = applyAttrSnapshotToProps(
          ((current as any)?.props || {}) as Record<string, unknown>,
          propsPatch,
          finalChanges
        ) as any;
        const storeChanges = { ...(finalChanges as any), style: stylePatch, props: mergedProps } as Partial<Node>;
        store.updateNode(nodeId, storeChanges);
      }
    } else {
      logger.warn(`⚠️ NO YDOC: applyNodeChange for ${nodeId} writing to store only — changes will NOT persist!`, { changedKeys: Object.keys(finalChanges) });
      const store = useDiagramStore.getState();
      const current = store.nodes.find(n => n.id === nodeId);
      const { stylePatch, propsPatch } = computeStyleAndPropsPatches(current, finalChanges);
      const mergedProps = applyAttrSnapshotToProps(
        ((current as any)?.props || {}) as Record<string, unknown>,
        propsPatch,
        finalChanges
      ) as any;
      const storeChanges = { ...(finalChanges as any), style: stylePatch, props: mergedProps } as Partial<Node>;
      store.updateNode(nodeId, storeChanges);
    }

    // Sync changes to sibling shapes bound to the same data record.
    // Skip entirely when only position/structural properties changed (x, y, w, h, etc.)
    const changedKeys = Object.keys(finalChanges);
    const hasDataKeys = changedKeys.some(k => !EXCLUDED_FROM_SYNC.has(k));
    if (hasDataKeys) {
      this.syncSiblingBoundShapes(nodeId, finalChanges);
    }

    return {
      nodeId,
      oldState,
      newState
    };
  }

  /**
   * Sync property changes to sibling shapes bound to the same data record.
   * When multiple shapes are bound to the same sourceId + recordId, changes to one
   * should propagate to all others.
   *
   * With the centralized BoundRecordStore architecture:
   * - Local changes are stored in the CENTRAL store (not per-shape)
   * - This method only updates the VISUAL properties of siblings
   * - All shapes read their "effective" state from the central store
   */
  private syncSiblingBoundShapes(nodeId: string, changes: Partial<Node>): void {
    if (!this.ydoc) return;

    const shapesMap = new ShapePersistenceGateway(new ShapeMapAdapter(this.ydoc.getMap('shapes')));
    const shape = shapesMap.get(nodeId) as Record<string, unknown> | undefined;
    if (!shape) return;

    const binding = getDataBinding(shape);
    if (!binding) return;

    // Extract data-related changes using field mappings
    const dataSourceChanges = extractDataChanges(
      changes as Record<string, unknown>,
      undefined,
      binding.cachedRecord
    );

    if (Object.keys(dataSourceChanges).length === 0) return;

    // Convert data source changes back to shape properties for visual sync
    const { topLevel: shapeChanges, attrs: attrChanges } = createShapeUpdateFromData(dataSourceChanges);
    console.log('[syncSiblingBoundShapes] Shape update from data:', { shapeChanges, attrChanges });

    if (Object.keys(shapeChanges).length === 0 && Object.keys(attrChanges).length === 0) {
      console.log('[syncSiblingBoundShapes] No shape updates to apply');
      return;
    }

    // Find sibling shapes bound to the same record
    const siblings = findSiblingBoundShapes(
      this.ydoc,
      binding.sourceId,
      binding.recordId,
      nodeId
    );

    console.log('[syncSiblingBoundShapes] Found siblings:', {
      count: siblings.length,
      siblingIds: siblings.map(s => s.shapeId),
    });

    if (siblings.length === 0) return;

    console.log('[syncSiblingBoundShapes] Syncing visual changes to sibling bound shapes', {
      sourceShapeId: nodeId,
      siblingCount: siblings.length,
      shapeChanges,
      attrChanges,
    });

    // Update siblings' VISUAL properties within a single transaction
    // Note: We don't update per-sibling localChanges anymore - the central store is the source of truth
    this.ydoc.transact(() => {
      for (const sibling of siblings) {
        const siblingAttrs = (sibling.shape.attrs || {}) as Record<string, unknown>;
        const siblingProps = (sibling.shape.props || {}) as Record<string, unknown>;
        const siblingPropsAttrs = (siblingProps.attrs || {}) as Record<string, unknown>;

        shapesMap.patch(sibling.shapeId, {
          ...sibling.shape,
          ...shapeChanges,
          attrs: {
            ...siblingAttrs,
            ...attrChanges,
          },
          props: {
            ...siblingProps,
            ...shapeChanges,
            attrs: {
              ...siblingPropsAttrs,
              ...attrChanges,
            },
            // Keep the existing dataBinding - it references the same record
            // and the central store has the local changes
            dataBinding: sibling.binding,
          },
        });
      }
    }, 'sibling-sync');
  }
  
  /**
   * Revert a previously recorded change
   */
  restoreProjectedPlacement(record: NodeChangeRecord, undo: boolean): void {
    if (!this.ydoc || record.projectedLayoutDoc !== this.ydoc) return;
    const saved = readProjectedPosition(this.ydoc.getMap(PROJECTED_POSITIONS_MAP).get(record.nodeId));
    const expected = undo ? record.newState : record.oldState;
    const desired = undo ? record.oldState : record.newState;
    if (!saved || (typeof expected.x === 'number' && expected.x !== saved.x)
      || (typeof expected.y === 'number' && expected.y !== saved.y)) return;
    const position = readProjectedPosition({ ...saved, ...desired });
    if (!position) return;
    this.ydoc.transact(() => writeProjectedPosition(this.ydoc!, record.nodeId, position), 'local');
    // Another collaborator may have switched to Automatic since this drag.
    // Undo changes the saved Custom arrangement, not that automatic scene.
    if (this.ydoc.getMap(PROJECTED_LAYOUT_MAP).get('mode') === 'custom') {
      useDiagramStore.getState().updateNode(record.nodeId, position);
    }
  }

  revertNodeChange(record: NodeChangeRecord): void {
    if (record.projectedLayoutDoc) {
      this.restoreProjectedPlacement(record, true);
      return;
    }
    const oldAttrsAll: Record<string, unknown> = ((record.oldState as any)?.attrs || {}) as any;
    const newAttrsAll: Record<string, unknown> = ((record.newState as any)?.attrs || {}) as any;

    // Determine which style keys were actually modified by this command.
    // Keys that have the same value in both oldState and newState were merely
    // carried over (full-snapshot attrs) and must NOT be touched during revert.
    const changedStyleKeys = new Set<string>();
    STYLE_KEYS.forEach(k => {
      const inOldTop = Object.prototype.hasOwnProperty.call(record.oldState as any, k);
      const inNewTop = Object.prototype.hasOwnProperty.call(record.newState as any, k);
      const inOldAttr = Object.prototype.hasOwnProperty.call(oldAttrsAll, k as string);
      const inNewAttr = Object.prototype.hasOwnProperty.call(newAttrsAll, k as string);
      if (inOldTop !== inNewTop || (record.oldState as any)?.[k] !== (record.newState as any)?.[k] ||
          inOldAttr !== inNewAttr || oldAttrsAll[k as string] !== newAttrsAll[k as string]) {
        changedStyleKeys.add(k as string);
      }
    });

    const changedAttrKeys = collectChangedAttrKeysBetweenAttrSnapshots(oldAttrsAll, newAttrsAll);

    // WRITE TO YIJS (source of truth)
    if (this.ydoc) {
      const shapesMap = new ShapePersistenceGateway(new ShapeMapAdapter(this.ydoc.getMap('shapes')));
      const currentYjsNode = shapesMap.get(record.nodeId) as any;

      if (currentYjsNode) {
        this.ydoc.transact(() => {
          const baseRevert: any = { ...currentYjsNode, ...record.oldState };

          const prevProps: any = (currentYjsNode as any).props || {};
          const prevAttrs: Record<string, unknown> = (prevProps.attrs || {}) as any;
          const oldAttrs = oldAttrsAll;
          const newAttrs = newAttrsAll;

          const styleKeys = STYLE_KEYS;

          const nextProps: any = { ...prevProps };
          styleKeys.forEach((k) => {
            if (!changedStyleKeys.has(k as string)) return;
            const hasTop = Object.prototype.hasOwnProperty.call(record.oldState as any, k);
            const hasAttr = Object.prototype.hasOwnProperty.call(oldAttrs, k as string);
            const hasNewTop = Object.prototype.hasOwnProperty.call(record.newState as any, k);
            const hasNewAttr = Object.prototype.hasOwnProperty.call(newAttrs, k as string);
            const wasInOldState = hasTop || hasAttr;
            if (wasInOldState) {
              const fromTop = (record.oldState as any)?.[k];
              const fromAttrs = oldAttrs[k as string];
              const v = hasTop ? fromTop : fromAttrs;
              if (v === undefined) {
                if (k in nextProps) delete nextProps[k as string];
              } else {
                nextProps[k as string] = v as unknown;
              }
            } else if (hasNewTop || hasNewAttr) {
              if (k in nextProps) delete nextProps[k as string];
            }
          });
          const nextAttrs: Record<string, unknown> = { ...prevAttrs };
          changedAttrKeys.forEach((k) => {
            const hasOldAttr = Object.prototype.hasOwnProperty.call(oldAttrs, k);
            const hasNewAttr = Object.prototype.hasOwnProperty.call(newAttrs, k);
            const v = oldAttrs[k];
            if (hasOldAttr && v === undefined) {
              if (k in nextAttrs) delete nextAttrs[k];
            } else if (hasOldAttr) {
              nextAttrs[k] = v as unknown;
            } else if (hasNewAttr) {
              if (k in nextAttrs) delete nextAttrs[k];
            }
          });
          Object.keys(oldAttrs).forEach((k) => {
            if (!(k in nextAttrs)) nextAttrs[k] = (oldAttrs as any)[k];
          });
          if (Object.keys(nextAttrs).length > 0) {
            nextProps.attrs = nextAttrs;
          } else if ('attrs' in nextProps) {
            delete nextProps.attrs;
          }

          const nextStyle: Record<string, unknown> = { ...(currentYjsNode?.style || {}) };
          styleKeys.forEach((k) => {
            if (!changedStyleKeys.has(k as string)) return;
            const hasTop = Object.prototype.hasOwnProperty.call(record.oldState as any, k);
            const hasAttr = Object.prototype.hasOwnProperty.call(oldAttrs, k as string);
            const hasNewTop = Object.prototype.hasOwnProperty.call(record.newState as any, k);
            const hasNewAttr = Object.prototype.hasOwnProperty.call(newAttrs, k as string);
            const wasInOldState = hasTop || hasAttr;
            if (wasInOldState) {
              const fromTop = (record.oldState as any)?.[k];
              const fromAttrs = oldAttrs[k as string];
              const v = hasTop ? fromTop : fromAttrs;
              if (v !== undefined) {
                nextStyle[k as string] = v as unknown;
              } else {
                delete nextStyle[k as string];
              }
            } else if (hasNewTop || hasNewAttr) {
              delete nextStyle[k as string];
            }
          });

          const topLevelPatch: Record<string, unknown> = {};
          styleKeys.forEach((k) => {
            if (!changedStyleKeys.has(k as string)) return;
            const hasTop = Object.prototype.hasOwnProperty.call(record.oldState as any, k);
            if (hasTop) {
              const vTop = (record.oldState as any)?.[k];
              topLevelPatch[k as string] = vTop;
            }
          });

          const revertedNode = { ...baseRevert, ...topLevelPatch, style: nextStyle, props: nextProps };
          shapesMap.patch(record.nodeId, revertedNode);
        });
      } else {
        logger.debug(`Node ${record.nodeId} not in Yjs yet, using store fallback for revert`);
        this.revertNodeViaStore(record, changedStyleKeys, changedAttrKeys);
      }
    } else {
      logger.debug(`Local-only mode: reverting in store (Yjs will sync when ready)`);
      this.revertNodeViaStore(record, changedStyleKeys, changedAttrKeys);
    }
  }

  /**
   * Store-only revert helper shared by both store fallback paths.
   */
  private revertNodeViaStore(
    record: NodeChangeRecord,
    changedStyleKeys: Set<string>,
    changedAttrKeys: Set<string>,
  ): void {
    const store = useDiagramStore.getState();
    const current = store.nodes.find(n => n.id === record.nodeId) as any;
    const prevProps = (current?.props || {}) as Record<string, unknown>;
    const prevAttrs = (current?.attrs || {}) as Record<string, unknown>;
    const oldAttrs = ((record.oldState as any)?.attrs || {}) as Record<string, unknown>;
    const newAttrs = ((record.newState as any)?.attrs || {}) as Record<string, unknown>;
    const styleKeys = STYLE_KEYS;

    const nextProps: Record<string, unknown> = { ...prevProps };
    styleKeys.forEach((k) => {
      if (!changedStyleKeys.has(k as string)) return;
      const hasTop = Object.prototype.hasOwnProperty.call(record.oldState as any, k);
      const hasAttr = Object.prototype.hasOwnProperty.call(oldAttrs, k as string);
      const hasNewTop = Object.prototype.hasOwnProperty.call(record.newState as any, k);
      const hasNewAttr = Object.prototype.hasOwnProperty.call(newAttrs, k as string);
      const wasInOldState = hasTop || hasAttr;
      if (wasInOldState) {
        const fromTop = (record.oldState as any)?.[k];
        const fromAttrs = oldAttrs[k as string];
        const v = hasTop ? fromTop : fromAttrs;
        if (v === undefined) {
          if (k in nextProps) delete nextProps[k as string];
        } else {
          nextProps[k as string] = v as unknown;
        }
      } else if (hasNewTop || hasNewAttr) {
        if (k in nextProps) delete nextProps[k as string];
      }
    });
    const nextAttrs: Record<string, unknown> = { ...prevAttrs };
    changedAttrKeys.forEach((k) => {
      const hasOldAttr = Object.prototype.hasOwnProperty.call(oldAttrs, k);
      const hasNewAttr = Object.prototype.hasOwnProperty.call(newAttrs, k);
      const v = oldAttrs[k];
      if (hasOldAttr && v === undefined) {
        if (k in nextAttrs) delete nextAttrs[k];
      } else if (hasOldAttr) {
        nextAttrs[k] = v as unknown;
      } else if (hasNewAttr) {
        if (k in nextAttrs) delete nextAttrs[k];
      }
    });
    Object.keys(oldAttrs).forEach((k) => {
      if (!(k in nextAttrs)) nextAttrs[k] = (oldAttrs as any)[k];
    });

    const mergedOldState: any = { ...record.oldState } as Record<string, unknown>;
    mergedOldState.attrs = nextAttrs;
    // Keep the projection mirror consistent with the restored attribute snapshot.
    nextProps.attrs = nextAttrs;
    mergedOldState.props = nextProps;
    styleKeys.forEach((k) => {
      if (!changedStyleKeys.has(k as string)) return;
      const hasTop = Object.prototype.hasOwnProperty.call(record.oldState as any, k);
      const vTop = (record.oldState as any)?.[k];
      (mergedOldState as any)[k] = hasTop ? vTop : undefined;
    });
    store.updateNode(record.nodeId, mergedOldState);
  }
  
  /**
   * Get a node by ID - always returns fresh state
   */
  getNode(nodeId: string): Node | undefined {
    // Force fresh state read
    const store = useDiagramStore.getState();
    const node = store.nodes.find(n => n.id === nodeId);
    logger.info(`Retrieved node ${nodeId}:`, node);
    return node;
  }
  
  /**
   * Add a node to Yjs (source of truth)
   */
	  addNode(node: Node): NodeChangeRecord {
	    const invalidGeometry = getInvalidShapeCreationGeometry(node as unknown as Record<string, unknown>);
	    if (invalidGeometry.length > 0) {
	      throw new InvalidShapeGeometryError(node.id, invalidGeometry);
	    }
	    // Ensure zIndex is assigned for new nodes without explicit zIndex
	    const storeSnapshot = useDiagramStore.getState();
	    const isNew = !storeSnapshot.nodes.some(n => n.id === node.id);
	    const maxZ = storeSnapshot.nodes.reduce((m, n) => Math.max(m, Number((n as any).zIndex ?? 0)), 0);
	    const minZ = storeSnapshot.nodes.reduce((m, n) => Math.min(m, Number((n as any).zIndex ?? 0)), 0);
	    const nodeToAdd = (isNew && (node as any).zIndex == null)
	      ? {
	          ...node,
	          zIndex:
	            (node as any)?.type === 'table'
	              ? (Number.isFinite(minZ) ? minZ : 0) - 1
	              : (Number.isFinite(maxZ) ? maxZ : 0) + 1,
	        }
	      : node;

    // WRITE TO YIJS (source of truth)
    if (this.ydoc) {
      const shapesMap = new ShapeMapAdapter(this.ydoc.getMap('shapes'));
      const shapeGateway = new ShapePersistenceGateway(shapesMap);
      this.ydoc.transact(() => {
        // Mirror properties into props structure for projection compatibility
        // This matches the pattern used in applyNodeChange for updates
        const props: any = { ...(nodeToAdd as any).props };

        // Mirror all top-level properties into props (except structural properties)
        const structuralKeys = new Set(['id', 'type', 'category', 'x', 'y', 'w', 'h', 'width', 'height', 'zIndex', 'style', 'props']);
        Object.keys(nodeToAdd).forEach((key) => {
          if (!structuralKeys.has(key)) {
            props[key] = (nodeToAdd as any)[key];
          }
        });

        // Ensure attrs are in props if present
        if ((nodeToAdd as any).attrs) {
          props.attrs = (nodeToAdd as any).attrs;
        }

        const nodeWithProps = {
          ...nodeToAdd,
          props
        };

        logger.info('Storing node with props:', {
          id: nodeToAdd.id,
          name: (nodeToAdd as any).name,
          propsKeys: Object.keys(props),
          hasMedications: 'medications' in props,
          medications: props.medications,
          hasGeneticMarkers: 'geneticMarkers' in props,
          geneticMarkers: props.geneticMarkers
        });

        try {
          logger.debug('Debug SDI: NodeMutationManager.addNode → Yjs', {
            id: nodeToAdd.id,
            type: (nodeToAdd as any)?.type,
            x: (nodeToAdd as any)?.x,
            y: (nodeToAdd as any)?.y
          });
        } catch {}
        shapeGateway.create(nodeWithProps);
      });
    } else {
      logger.warn(`⚠️ No Yjs doc - adding to store only (will NOT persist!)`);
      try { logger.debug('Debug SDI: NodeMutationManager.addNode without Yjs', { id: (nodeToAdd as any)?.id }); } catch {}
      const store = useDiagramStore.getState();
      store.addNode(nodeToAdd, true);
    }

    return {
      nodeId: nodeToAdd.id,
      oldState: {},
      newState: nodeToAdd
    };
  }

  /**
   * Batch add multiple nodes in a single Yjs transaction
   */
	  batchAddNodes(nodes: Node[]): NodeChangeRecord[] {
	    if (nodes.length === 0) return [];
	    for (const node of nodes) {
	      const invalidGeometry = getInvalidShapeCreationGeometry(node as unknown as Record<string, unknown>);
	      if (invalidGeometry.length > 0) {
	        throw new InvalidShapeGeometryError(node.id, invalidGeometry);
	      }
	    }
	    
	    const storeSnapshot = useDiagramStore.getState();
	    const maxZ = storeSnapshot.nodes.reduce((m, n) => Math.max(m, Number((n as any).zIndex ?? 0)), 0);
	    const minZ = storeSnapshot.nodes.reduce((m, n) => Math.min(m, Number((n as any).zIndex ?? 0)), 0);
	    const records: NodeChangeRecord[] = [];
    
    // Prepare nodes with zIndex
	    const nodesToAdd = nodes.map(node => {
	      const isNew = !storeSnapshot.nodes.some(n => n.id === node.id);
	      return (isNew && (node as any).zIndex == null)
	        ? {
	            ...node,
	            zIndex:
	              (node as any)?.type === 'table'
	                ? (Number.isFinite(minZ) ? minZ : 0) - 1
	                : (Number.isFinite(maxZ) ? maxZ : 0) + 1,
	          }
	        : node;
	    });
    
    // WRITE TO YIJS (source of truth) in a single batch transaction
    if (this.ydoc) {
      const shapesMap = new ShapeMapAdapter(this.ydoc.getMap('shapes'));
      const shapeGateway = new ShapePersistenceGateway(shapesMap);
      this.ydoc.transact(() => {
        nodesToAdd.forEach(nodeToAdd => {
          // Mirror properties into props structure for projection compatibility
          const props: any = { ...(nodeToAdd as any).props };
          const structuralKeys = new Set(['id', 'type', 'category', 'x', 'y', 'w', 'h', 'width', 'height', 'zIndex', 'style', 'props']);
          Object.keys(nodeToAdd).forEach((key) => {
            if (!structuralKeys.has(key)) {
              props[key] = (nodeToAdd as any)[key];
            }
          });
          if ((nodeToAdd as any).attrs) {
            props.attrs = (nodeToAdd as any).attrs;
          }
          const nodeWithProps = { ...nodeToAdd, props };
          try { logger.debug('Debug SDI: NodeMutationManager.batchAddNodes → Yjs', { id: nodeToAdd.id }); } catch {}
          shapeGateway.create(nodeWithProps);
        });
      }, 'local'); // Use 'local' origin to distinguish from electric/awareness/server
      
      // DO NOT update store here - let Yjs → Store sync handle it
      // This prevents triggering store-to-Yjs sync which would create a second transaction
    } else {
      logger.warn(`⚠️ No Yjs doc - adding to store only (will NOT persist!)`);
      const store = useDiagramStore.getState();
      nodesToAdd.forEach(node => store.addNode(node, true));
    }
    
    nodesToAdd.forEach(node => {
      records.push({
        nodeId: node.id,
        oldState: {},
        newState: node
      });
    });
    
    return records;
  }
  
  /**
   * Delete a node from Yjs (source of truth)
   */
  deleteNode(nodeId: string): NodeChangeRecord {
    
    const store = useDiagramStore.getState();
    const currentNode = store.nodes.find(n => n.id === nodeId);
    
    const oldState = currentNode ? { ...currentNode } : {};
    
    // WRITE TO YIJS (source of truth)
    if (this.ydoc) {
      const shapesMap = new ShapePersistenceGateway(new ShapeMapAdapter(this.ydoc.getMap('shapes')));
      const binding = currentNode ? getDataBinding(currentNode as unknown as Record<string, unknown>) : null;
      const hasRemainingSibling = !!binding && store.nodes.some((node) => {
        if (node.id === nodeId) return false;
        const nodeBinding = getDataBinding(node as unknown as Record<string, unknown>);
        return (
          nodeBinding?.sourceId === binding.sourceId &&
          nodeBinding?.recordId === binding.recordId
        );
      });
      this.ydoc.transact(() => {
        if (binding && !hasRemainingSibling) {
          trackDeletedNodeBinding(this.ydoc!, nodeId, binding);
        }
        try { logger.debug('Debug SDI: NodeMutationManager.deleteNode → Yjs', { id: nodeId }); } catch {}
        shapesMap.delete(nodeId);
      });
      // Optimistically update local store for immediate UI feedback
      store.deleteNode(nodeId, true);
    } else {
      logger.debug(`Local-only mode: deleting node from store (Yjs will sync when ready)`);
      try { logger.debug('Debug SDI: NodeMutationManager.deleteNode without Yjs', { id: nodeId }); } catch {}
      store.deleteNode(nodeId);
    }
    
    return {
      nodeId,
      oldState,
      newState: {}
    };
  }

  /**
   * Batch delete multiple nodes in a single Yjs transaction
   */
  batchDeleteNodes(nodeIds: string[]): NodeChangeRecord[] {
    if (nodeIds.length === 0) return [];
    
    const store = useDiagramStore.getState();
    const records: NodeChangeRecord[] = [];
    
    // Collect old states
    nodeIds.forEach(nodeId => {
      const currentNode = store.nodes.find(n => n.id === nodeId);
      if (currentNode) {
        records.push({
          nodeId,
          oldState: { ...currentNode },
          newState: {}
        });
      }
    });
    
    // WRITE TO YIJS (source of truth) in a single batch transaction
    if (this.ydoc) {
      const shapesMap = new ShapePersistenceGateway(new ShapeMapAdapter(this.ydoc.getMap('shapes')));
      const deleteSet = new Set(nodeIds);
      const bindingsToTrack = records.flatMap((record) => {
        const binding = getDataBinding(record.oldState as Record<string, unknown>);
        if (!binding || binding.isNewRecord) return [];

        const hasRemainingSibling = store.nodes.some((node) => {
          if (deleteSet.has(node.id)) return false;
          const nodeBinding = getDataBinding(node as unknown as Record<string, unknown>);
          return (
            nodeBinding?.sourceId === binding.sourceId &&
            nodeBinding?.recordId === binding.recordId
          );
        });
        return hasRemainingSibling ? [] : [{ nodeId: record.nodeId, binding }];
      });

      this.ydoc.transact(() => {
        bindingsToTrack.forEach(({ nodeId, binding }) => {
          trackDeletedNodeBinding(this.ydoc!, nodeId, binding);
        });
        nodeIds.forEach(nodeId => {
          try { logger.debug('Debug SDI: NodeMutationManager.batchDeleteNodes → Yjs', { id: nodeId }); } catch {}
          shapesMap.delete(nodeId);
        });
      }, 'local'); // Use 'local' origin to distinguish from electric/awareness/server
      
      // DO NOT update store here - let Yjs → Store sync handle it
      // This prevents triggering store-to-Yjs sync which would create a second transaction
    } else {
      logger.debug(`Local-only mode: deleting nodes from store (Yjs will sync when ready)`);
      nodeIds.forEach(nodeId => {
        store.deleteNode(nodeId);
      });
    }
    
    return records;
  }
  
  /**
   * Restore a node from a change record (for undo)
   */
  restoreNode(record: NodeChangeRecord): void {
    if (Object.keys(record.oldState).length > 0) {
      // Restore previous state
      const node = record.oldState as Node;
      
      if (this.ydoc) {
        const shapesMap = new ShapePersistenceGateway(new ShapeMapAdapter(this.ydoc.getMap('shapes')));
        this.ydoc.transact(() => {
          clearDeletedNodeBindings(this.ydoc!, [record.nodeId]);
          shapesMap.upsert(node as unknown as Record<string, unknown>);
        });
      } else {
        const store = useDiagramStore.getState();
        store.addNode(node, true);
      }
    }
  }
  
  // ============ EDGE MUTATION METHODS ============
  
  /**
   * Add an edge to Yjs (source of truth)
   */
  addEdge(edge: Edge): EdgeChangeRecord {
    
    // WRITE TO YIJS (source of truth)
    if (this.ydoc) {
      const connectorsMap = this.ydoc.getMap('connectors');
      this.ydoc.transact(() => {
        try { logger.debug('Debug SDI: NodeMutationManager.addEdge → Yjs', { id: edge.id, from: (edge as any)?.from, to: (edge as any)?.to }); } catch {}
        connectorsMap.set(edge.id, edge);
      });
    } else {
      logger.warn(`⚠️ No Yjs doc - adding to store only (will NOT persist!)`);
      try { logger.debug('Debug SDI: NodeMutationManager.addEdge without Yjs', { id: edge.id }); } catch {}
      const store = useDiagramStore.getState();
      store.addEdge(edge, true);
    }
    
    return {
      edgeId: edge.id,
      oldState: null,
      newState: edge
    };
  }

  /**
   * Batch add multiple edges in a single Yjs transaction
   */
  batchAddEdges(edges: Edge[]): EdgeChangeRecord[] {
    if (edges.length === 0) return [];
    
    const records: EdgeChangeRecord[] = [];
    
    // WRITE TO YIJS (source of truth) in a single batch transaction
    if (this.ydoc) {
      const connectorsMap = this.ydoc.getMap('connectors');
      this.ydoc.transact(() => {
        edges.forEach(edge => {
          try { logger.debug('Debug SDI: NodeMutationManager.batchAddEdges → Yjs', { id: edge.id }); } catch {}
          connectorsMap.set(edge.id, edge);
        });
      }, 'local'); // Use 'local' origin to distinguish from electric/awareness/server
      
      // DO NOT update store here - let Yjs → Store sync handle it
      // This prevents triggering store-to-Yjs sync which would create a second transaction
    } else {
      logger.warn(`⚠️ No Yjs doc - adding to store only (will NOT persist!)`);
      const store = useDiagramStore.getState();
      edges.forEach(edge => store.addEdge(edge, true));
    }
    
    edges.forEach(edge => {
      records.push({
        edgeId: edge.id,
        oldState: null,
        newState: edge
      });
    });
    
    return records;
  }
  
  /**
   * Update an edge in Yjs (source of truth)
   */
  updateEdge(edgeId: string, updates: Partial<Edge>): EdgeChangeRecord {
    const store = useDiagramStore.getState();
    const currentEdge = store.edges.find(e => e.id === edgeId);
    
    if (!currentEdge) {
      logger.error(`Edge ${edgeId} not found!`);
      throw new Error(`Edge ${edgeId} not found`);
    }
    
    const oldState = { ...currentEdge };

    // If attrs are provided, prepare a patch that clears keys absent in incoming attrs
    let updatesForStore: Partial<Edge> = updates;
    const incomingAttrs = (updates as any)?.attrs as Record<string, unknown> | undefined;
    if (incomingAttrs) {
      const prev = (currentEdge.attrs || {}) as Record<string, unknown>;
      const patch: Record<string, unknown> = {};
      // Clear keys that existed previously but are not present now
      Object.keys(prev).forEach((k) => {
        if (!(k in incomingAttrs)) {
          patch[k] = undefined;
        }
      });
      // Apply incoming values
      Object.keys(incomingAttrs).forEach((k) => {
        patch[k] = incomingAttrs[k];
      });
      updatesForStore = { ...updates, attrs: patch } as Partial<Edge>;
    }

    // Build a final merged state to write to Yjs that mirrors how the store
    // merges connector attrs (so undo/redo for styling like color is stable).
    // Important: always deep-merge attrs to avoid replacing the whole object
    // when only a subset of fields is provided.
    const mergedAttrsForYjs = {
      ...(currentEdge.attrs || {}),
      ...((updatesForStore as any)?.attrs || {}),
    } as Record<string, unknown>;
    const newState = {
      ...currentEdge,
      ...updatesForStore,
      // Ensure attrs reflect the deep merge result
      attrs: mergedAttrsForYjs as any,
    } as Edge;
    
    // WRITE TO YIJS (source of truth)
    if (this.ydoc) {
      const connectorsMap = this.ydoc.getMap('connectors');
      const currentYjsEdge = connectorsMap.get(edgeId);
      
      if (currentYjsEdge) {
        this.ydoc.transact(() => {
          connectorsMap.set(edgeId, newState);
        });
        // Optimistically update store for immediate UI feedback
        // The Yjs observer will also sync, but this ensures no delay
        store.updateEdge(edgeId, updatesForStore, true);
      } else {
        // Edge not in Yjs yet - add it to Yjs and update store with fromYjs=true to bypass canWrite check
        logger.debug(`Edge ${edgeId} not in Yjs yet, adding to Yjs and updating store`);
        this.ydoc.transact(() => {
          connectorsMap.set(edgeId, newState);
        });
        store.updateEdge(edgeId, updatesForStore, true);
      }
    } else {
      // No Yjs doc - update store directly with fromYjs=true to bypass canWrite check for command-based updates
      logger.debug(`Local-only mode: updating edge in store (Yjs will sync when ready)`);
      store.updateEdge(edgeId, updatesForStore, true);
    }
    
    return {
      edgeId,
      oldState,
      newState
    };
  }
  
  /**
   * Delete an edge from Yjs (source of truth)
   */
  deleteEdge(edgeId: string): EdgeChangeRecord {
    
    const store = useDiagramStore.getState();
    const currentEdge = store.edges.find(e => e.id === edgeId);
    
    const oldState = currentEdge ? { ...currentEdge } : null;
    
    // WRITE TO YIJS (source of truth)
    if (this.ydoc) {
      const connectorsMap = this.ydoc.getMap('connectors');
      this.ydoc.transact(() => {
        try { logger.debug('Debug SDI: NodeMutationManager.deleteEdge → Yjs', { id: edgeId }); } catch {}
        connectorsMap.delete(edgeId);
      });
      // Optimistically update local store for immediate UI feedback
      store.deleteEdge(edgeId, true);
    } else {
      logger.debug(`Local-only mode: deleting edge from store (Yjs will sync when ready)`);
      try { logger.debug('Debug SDI: NodeMutationManager.deleteEdge without Yjs', { id: edgeId }); } catch {}
      store.deleteEdge(edgeId);
    }
    
    return {
      edgeId,
      oldState,
      newState: {}
    };
  }

  /**
   * Batch delete multiple edges in a single Yjs transaction
   */
  batchDeleteEdges(edgeIds: string[]): EdgeChangeRecord[] {
    if (edgeIds.length === 0) return [];
    
    const store = useDiagramStore.getState();
    const records: EdgeChangeRecord[] = [];
    
    // Collect old states
    edgeIds.forEach(edgeId => {
      const currentEdge = store.edges.find(e => e.id === edgeId);
      if (currentEdge) {
        records.push({
          edgeId,
          oldState: { ...currentEdge },
          newState: {}
        });
      }
    });
    
    // WRITE TO YIJS (source of truth) in a single batch transaction
    if (this.ydoc) {
      const connectorsMap = this.ydoc.getMap('connectors');
      this.ydoc.transact(() => {
        edgeIds.forEach(edgeId => {
          try { logger.debug('Debug SDI: NodeMutationManager.batchDeleteEdges → Yjs', { id: edgeId }); } catch {}
          connectorsMap.delete(edgeId);
        });
      }, 'local'); // Use 'local' origin to distinguish from electric/awareness/server
      
      // DO NOT update store here - let Yjs → Store sync handle it
      // This prevents triggering store-to-Yjs sync which would create a second transaction
    } else {
      logger.debug(`Local-only mode: deleting edges from store (Yjs will sync when ready)`);
      edgeIds.forEach(edgeId => {
        store.deleteEdge(edgeId);
      });
    }
    
    return records;
  }
  
  /**
   * Restore an edge from a change record (for undo)
   */
  restoreEdge(record: EdgeChangeRecord): void {
    if (record.oldState) {
      // Restore previous state
      const edge = record.oldState as Edge;
      
      if (this.ydoc) {
        const connectorsMap = this.ydoc.getMap('connectors');
        this.ydoc.transact(() => {
          connectorsMap.set(record.edgeId, edge);
        });
      } else {
        const store = useDiagramStore.getState();
        store.addEdge(edge);
      }
    }
  }
  
  /**
   * Get an edge by ID - always returns fresh state
   */
  getEdge(edgeId: string): Edge | undefined {
    const store = useDiagramStore.getState();
    return store.edges.find(e => e.id === edgeId);
  }
  
  /**
   * Static method for singleton access
   */
  static getInstance(): NodeMutationManager {
    return getNodeMutationManager();
  }
}

/**
 * Singleton instance of the mutation manager
 * This ensures all commands use the same mutation manager
 */
let mutationManagerInstance: NodeMutationManager | null = null;

export function getNodeMutationManager(): NodeMutationManager {
  if (!mutationManagerInstance) {
    mutationManagerInstance = new NodeMutationManager();
    // Register globally for BaseNodeCommand to access
    (window as any).__nodeMutationManager = mutationManagerInstance;
  }
  return mutationManagerInstance;
}
